import type { Equity } from '../api/types';
import { stanceGlyph } from '../hooks/format';

/** The arc strip (docs/design/story-visualisation.md): stance marks over an equity hairline. */
export function Arc({ data, allocation, nextAt, onPick }: { data: Equity; allocation: number; nextAt: string | null; onPick?: (reportId: string) => void }) {
  const W = 360;
  const H = 56;
  const times = [...data.equity.map((e) => +new Date(e.at)), ...data.stances.map((s) => +new Date(s.at)), ...data.fills.map((f) => +new Date(f.at))];
  if (nextAt) times.push(+new Date(nextAt));
  const now = Date.now();
  times.push(now);
  const t0 = Math.min(...times);
  const t1 = Math.max(...times);
  const x = (t: number) => (t1 === t0 ? W / 2 : 8 + ((t - t0) / (t1 - t0)) * (W - 16));
  const eq = data.equity.map((e) => Number(e.equity));
  const lo = Math.min(allocation, ...eq);
  const hi = Math.max(allocation, ...eq);
  const y = (v: number) => (hi === lo ? 40 : 46 - ((v - lo) / (hi - lo)) * 20);
  const path = data.equity.length ? data.equity.map((e, i) => `${i ? 'L' : 'M'}${x(+new Date(e.at)).toFixed(1)},${y(Number(e.equity)).toFixed(1)}`).join(' ') : '';
  const last = eq.at(-1);
  const lastTone = last === undefined ? 'var(--fg-2)' : last >= allocation ? 'var(--gain)' : 'var(--loss)';
  return (
    <svg className="arc" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-label="narrative arc">
      <line x1={8} x2={W - 8} y1={40} y2={40} stroke="var(--line)" strokeWidth={1} />
      {path && <path d={path} fill="none" stroke="var(--fg-2)" strokeWidth={1.2} />}
      {data.equity.length > 1 && (
        <path d={`M${x(+new Date(data.equity.at(-2)!.at)).toFixed(1)},${y(eq.at(-2)!).toFixed(1)} L${x(+new Date(data.equity.at(-1)!.at)).toFixed(1)},${y(eq.at(-1)!).toFixed(1)}`} fill="none" stroke={lastTone} strokeWidth={1.6} />
      )}
      {data.fills.map((f, i) => (
        <line key={i} x1={x(+new Date(f.at))} x2={x(+new Date(f.at))} y1={36} y2={44} stroke={f.side === 'BUY' ? 'var(--accent)' : 'var(--paper)'} strokeWidth={1.5} />
      ))}
      {data.stances.map((s) => (
        <g key={s.report_id} onClick={() => onPick?.(s.report_id)} style={{ cursor: onPick ? 'pointer' : 'default' }}>
          <text x={x(+new Date(s.at))} y={20} textAnchor="middle" fontSize={12} fill={s.stance === 'INVALIDATED' ? 'var(--live)' : 'var(--fg)'}>
            {stanceGlyph(s.stance)}
          </text>
        </g>
      ))}
      <line x1={x(now)} x2={x(now)} y1={10} y2={50} stroke="var(--line)" strokeDasharray="2 3" />
      {nextAt && <circle cx={x(+new Date(nextAt))} cy={20} r={3.5} fill="none" stroke="var(--accent)" strokeWidth={1.2} />}
      <text x={8} y={H - 2} fontSize={9} fill="var(--fg-3)">{new Date(t0).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}</text>
      <text x={W - 8} y={H - 2} fontSize={9} fill="var(--fg-3)" textAnchor="end">{nextAt ? 'next' : 'now'}</text>
    </svg>
  );
}
